import articles from '../../services/products/articles';
import productService from '../../services/products';
import platform from '../../util/platform';
import { SkapaTheme } from '@inter-ikea-kompis/themes/lib';
import {
  KompisProductCard,
  KompisSheet,
  KompisSheetBody,
  KompisSheetBodyPadding,
  KompisSheetHeader,
} from '@inter-ikea-kompis/react-components';
import { SheetSizeEnum } from '@inter-ikea-kompis/component-sheet';
import classNames from 'classnames';
import styles from '../ConfMenu/ConfMenu.module.less';
import ProductCardLayoutEnum from '@inter-ikea-kompis/component-product-card/lib/enums/ProductCardLayoutEnum';
import { ProductCardMediaEnum } from '@inter-ikea-kompis/component-product-card';
import { ProductDescriptionProductMeasureEnum } from '@inter-ikea-kompis/utilities/lib';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { articleNo } from '../../util/articleNo';
import {
  selectIsLandscape,
  selectIsMobile,
} from '../../state/userAgent/userAgentSelectors';
import { selectKompisTranslations } from '../../state/translations/translationsSelectors';
import { selectDexfSettings } from '../../state/dexfSettings/dexfSettingsSelectors';
import { TacItem } from '../../generalTypes';
import {
  actionDequeueAllSheets,
  actionDequeueSheet,
} from '../../state/sheets/sheetActions';

export interface Props {
  item: TacItem;
  hasBackButton?: boolean;
}

export const ADDITIONAL_INFO = 'ADDITIONAL_INFO';

export const AdditionalInfoSheet: React.FunctionComponent<Props> = ({
  item,
  hasBackButton,
}) => {
  const dispatch = useDispatch();
  const isMobile = useSelector(selectIsMobile);
  const isLandscape = useSelector(selectIsLandscape);
  const kompisTranslations = useSelector(selectKompisTranslations);
  const dexfSettings = useSelector(selectDexfSettings);

  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [visibleModal, setVisibleModal] = React.useState<any>(undefined);

  const [sheetVisible, setSheetVisible] = React.useState(true);

  useEffect(() => {
    return () => setSheetVisible(false);
  }, []);

  const _createAdditionalInfoObject = () => {
    if (!item) {
      return null;
    }

    const itemToDisplay = productService.isOnlyAvailableInMultipack(item.id)
      ? productService.getMultipackProductOf(item.id)
      : item;

    if (!itemToDisplay) {
      return null;
    }

    const { itemno: id } = articleNo(itemToDisplay.id)
      ? { itemno: articleNo(itemToDisplay.id) }
      : itemToDisplay.iows[0];

    return {
      id: id.replace(/^S/i, ''),
      type: /^S/.test(id) ? 'SPR' : 'ART',
    };
  };

  const aiItem = _createAdditionalInfoObject();
  const kItem = articles.getArticle(aiItem?.id);

  const onBodyClick = (event: Event) => event.stopPropagation();

  const onCarouselChange = ({
    detail: { carouselSelectedIndex },
  }: CustomEvent) => setSelectedIndex(carouselSelectedIndex);

  const onModalOpen = ({ detail: { visibleModal } }: CustomEvent) =>
    setVisibleModal(visibleModal);

  const onModalClose = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();
    setVisibleModal(null);
  };

  const closeAllSheets = () => dispatch(actionDequeueAllSheets());
  const closeSheet = () => dispatch(actionDequeueSheet());

  return (
    <KompisSheet
      onOverlayClick={closeAllSheets}
      visible={sheetVisible}
      theme={SkapaTheme}
      sheetSize={SheetSizeEnum.small}
    >
      <KompisSheetHeader
        floatingHeader={isMobile && isLandscape}
        theme={SkapaTheme}
        heading={''}
        onCloseButtonClick={closeAllSheets}
        backIcon={hasBackButton}
        onBackButtonClick={closeSheet}
      />
      <KompisSheetBody
        theme={SkapaTheme}
        verticalScroll={false}
        className={classNames(styles.KompisSheetBody)}
        // @ts-ignore KompisSheetBody does not support click events
        onClick={onBodyClick}
      >
        <KompisSheetBodyPadding theme={SkapaTheme}>
          <KompisProductCard
            product={kItem}
            showDesignText={true}
            translations={kompisTranslations}
            settings={dexfSettings}
            disableLinks={platform.isKiosk}
            theme={SkapaTheme}
            layout={ProductCardLayoutEnum.information}
            carouselSelectedIndex={selectedIndex}
            onCarouselChange={onCarouselChange}
            visibleModal={visibleModal}
            onModalOpen={onModalOpen}
            onModalClose={onModalClose}
            carouselShowArrows={true}
            media={ProductCardMediaEnum.carousel}
            productMeasures={ProductDescriptionProductMeasureEnum.long}
            carouselShowScrollbar={true}
            showProductIdentifier={true}
          />
        </KompisSheetBodyPadding>
      </KompisSheetBody>
    </KompisSheet>
  );
};
